import React, { useEffect, useRef, useState } from "react";
import { Camera, Loader2, Store } from "lucide-react";
import ImageCropModal from "../../../../shared/ImageCropModal/ImageCropModal";
import "./styles/BusinessLogoUploader.css";

type Variant = "full" | "compact";

interface BusinessLogoUploaderProps {
  /** Existing logo URL (from server) — shown when no new file is staged. */
  existingLogoUrl: string | null;
  /** Fires when the user picks a valid file. */
  onFileSelected: (file: File) => void;
  /** Fires with a user-friendly reason when the picked file is rejected (wrong type / too large). */
  onInvalidFile?: (reason: string) => void;
  /** "full" — large circle with "Add Logo" placeholder (create/edit modal).
   *  "compact" — square slot that drops into existing layouts (header logo). */
  variant?: Variant;
  /** When true, dims the image and shows a spinner overlay. */
  isUploading?: boolean;
}

const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_SIZE_MB = 5;

const BusinessLogoUploader: React.FC<BusinessLogoUploaderProps> = ({
  existingLogoUrl,
  onFileSelected,
  onInvalidFile,
  variant = "full",
  isUploading = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [remoteFailed, setRemoteFailed] = useState(false);

  // Reset failure flag when the server URL changes (e.g. new business loaded)
  useEffect(() => {
    setRemoteFailed(false);
  }, [existingLogoUrl]);

  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
      if (cropSrc) URL.revokeObjectURL(cropSrc);
    };
  }, [localPreview, cropSrc]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      onInvalidFile?.("Please select a JPEG, PNG, or WEBP image.");
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      onInvalidFile?.(`Image must be smaller than ${MAX_SIZE_MB}MB.`);
      return;
    }

    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(URL.createObjectURL(file));
  };

  const handleCropComplete = (croppedBlob: Blob) => {
    setCropSrc(null);
    const croppedFile = new File([croppedBlob], "logo.jpg", {
      type: "image/jpeg",
    });

    if (localPreview) URL.revokeObjectURL(localPreview);
    setLocalPreview(URL.createObjectURL(croppedFile));
    onFileSelected(croppedFile);
  };

  const displayedSrc = localPreview ?? (remoteFailed ? null : existingLogoUrl);
  const rootClass = `biz-logo-uploader biz-logo-uploader--${variant} ${isUploading ? "is-uploading" : ""}`;

  return (
    <>
      <div
        className={rootClass}
        onClick={() => !isUploading && inputRef.current?.click()}
        role="button"
        aria-label="Upload business logo"
      >
        {displayedSrc ? (
          <img
            src={displayedSrc}
            alt="Business logo"
            className="biz-logo-preview"
            onError={() => setRemoteFailed(true)}
          />
        ) : variant === "full" ? (
          <div className="biz-logo-placeholder">
            <Camera size={28} color="#94a3b8" />
            <span>Add Logo</span>
          </div>
        ) : (
          <Store size={26} color="#6a11cb" />
        )}

        <div className="biz-logo-camera-badge">
          {isUploading ? (
            <Loader2 size={14} color="#fff" className="biz-logo-spinner" />
          ) : (
            <Camera size={14} color="#fff" />
          )}
        </div>

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="biz-logo-input"
          onChange={handleFileChange}
        />
      </div>

      {cropSrc && (
        <ImageCropModal
          imageSrc={cropSrc}
          aspectRatio={1}
          onCropComplete={handleCropComplete}
          onCancel={() => {
            if (cropSrc) URL.revokeObjectURL(cropSrc);
            setCropSrc(null);
            if (inputRef.current) inputRef.current.value = "";
          }}
        />
      )}
    </>
  );
};

export default BusinessLogoUploader;
